/* eslint-disable react-hooks/exhaustive-deps */
import { isEmpty, isEqual } from 'lodash'
import { FC, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Checkbox, FormControlLabel, Typography } from '@mui/material'
import { addDays } from 'date-fns'

import {
    ActionsButton,
    Box,
    BoxContainer,
    Grid,
    LabeledText,
    SelectInput,
    TextFieldLabel,
    TitleFirst
} from 'src/components/UI'
import DatePicker from 'src/components/UI/CustomUI/atoms/DatePicker'

import {
    companiesActions,
    employeesActions,
    jobActions
} from 'src/ducks/actions'
import {
    getEmployeesByRole,
    getJob,
    getTerritoriesSelector
} from 'src/ducks/selectors'

import { PrivateLoader } from 'src/components/templates'
import { getCompaniesMultipleField } from 'src/ducks/companies/selectors'
import { employeeIsReadOnly } from 'src/ducks/selectors'
import {
    AFFILIATE_TYPE_OPTIONS,
    calcWeekends,
    combinePreferredDateAndWindow,
    EMPLOYEE_ROLE_TYPE,
    formatDateTimestamp,
    formatTimestampDate,
    getDifferenceBetweenObjects,
    getDatesBetween,
    getHolidaysDate,
    getPreferredTimeWindow,
    STATUS,
    updateObjectByPath
} from 'src/helpers'
import { UpdateRequest } from '~api/types'
import { InformationTabsProps } from '../../types'

const Assignments: FC<InformationTabsProps> = ({ isChanged }) => {
  const isReadOnly = useSelector(employeeIsReadOnly)
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [pageLoading, setPageLoading] = useState(false)
  const [hasUpdatedData, setHasUpdatedData] = useState(false)

  const territoriesOptions = useSelector(getTerritoriesSelector)
  const affiliatesStages = useSelector(getCompaniesMultipleField)
  const territoryManagers = useSelector(
    getEmployeesByRole(EMPLOYEE_ROLE_TYPE.TERRITORY_MANAGER)
  )
  const territorySuccessManagers = useSelector(
    getEmployeesByRole(EMPLOYEE_ROLE_TYPE.TERRITORY_SUCCESS_MANAGER)
  )
  const supervisors = useSelector(
    getEmployeesByRole(EMPLOYEE_ROLE_TYPE.TERRITORY_SUPERVISOR)
  )
  const clientPersons = useSelector(
    getEmployeesByRole(EMPLOYEE_ROLE_TYPE.TERRITORY_CUSTOMER_CARE)
  )
  const superIntendents = useSelector(
    getEmployeesByRole(EMPLOYEE_ROLE_TYPE.TERRITORY_SUPERINTENDENT)
  )
  const clientProjectManagers = useSelector(
    getEmployeesByRole(EMPLOYEE_ROLE_TYPE.MANAGER)
  )
  const bdManagers = useSelector(
    getEmployeesByRole(EMPLOYEE_ROLE_TYPE.TERRITORY_BUSINESS_DEV_MANAGER)
  )

  const job = useSelector(getJob())
  const [jobInfo, setJobInfo] = useState(job)
  const [flexibleStartDate, setFlexibleStartDate] = useState(job?.properties?.preferredStartDate == null)
  const [flexibleCompletedOn, setFlexibleCompletedOn] = useState(job?.properties?.preferredCompletedOn == null)

  const timezone = job?.properties?.address?.timeZone
  const preferredWindow = getPreferredTimeWindow(jobInfo?.properties?.preferredStartDate, timezone)

  const handlePreferredWindowChange = (window: 'morning' | 'afternoon') => {
    const currentDate = jobInfo?.properties?.preferredStartDate ? formatTimestampDate(jobInfo.properties.preferredStartDate) : null
    const newWindow = preferredWindow === window ? undefined : window
    handleOnChangeValue('properties/preferredStartDate', combinePreferredDateAndWindow(currentDate, newWindow, timezone))
  }

  const holidays = getHolidaysDate()
  const nextDay = addDays(new Date(), 1)
  const weekends = calcWeekends(new Date(), addDays(new Date(), 400))
  const disabledDates = [...weekends, ...holidays, new Date(), nextDay]

  const disableInputs =
    job?.status === STATUS.COMPLETE || job?.status === STATUS.CLOSED || isReadOnly

  const fetchServices = useCallback(() => {
    dispatch(
      employeesActions.fetchEmployees(
        { page: 0, size: 150, searchParams: { approved: true } },
        (_succ: boolean) => {
          setLoading(false)
        }
      )
    )
  }, [dispatch])

  useEffect(() => {
    if (isEmpty(affiliatesStages)) {
      setPageLoading(true)
      dispatch(
        companiesActions.fetchCompanies({}, (_succ: boolean) =>
          setPageLoading(false)
        )
      )
    }
  }, [affiliatesStages])

  useEffect(() => {
    fetchServices()
    setJobInfo(job)
    setFlexibleStartDate(job?.properties?.preferredStartDate == null)
    setFlexibleCompletedOn(job?.properties?.preferredCompletedOn == null)
  }, [fetchServices, job])

  useEffect(() => {
    setHasUpdatedData(!isEqual(jobInfo, job))
  }, [jobInfo, job])

  const handleOnChangeValue = (path: string, newValue: string | number | boolean | null) => {
    setJobInfo(updateObjectByPath({ obj: jobInfo, path, value: newValue }))
  }

  const handleFlexibleStartDate = (flex: boolean) => {
    if (flex) {
      setFlexibleStartDate(true)
      handleOnChangeValue('properties/preferredStartDate', null)
    } else {
      setFlexibleStartDate(false)
    }
  }

  const handleFlexibleCompletedOn = (flex: boolean) => {
    if (flex) {
      setFlexibleCompletedOn(true)
      handleOnChangeValue('properties/preferredCompletedOn', null)
    } else {
      setFlexibleCompletedOn(false)
    }
  }

  const onSubmit = () => {
    setLoading(true)
    let jobDifferences: UpdateRequest = getDifferenceBetweenObjects(jobInfo, job, 'replace')

    // getDifferenceBetweenObjects skips null values (typeof null === 'object'),
    // so we manually add remove ops for nullable date fields
    const nullableProps = ['preferredStartDate', 'preferredCompletedOn'] as const
    for (const prop of nullableProps) {
      const newVal = jobInfo?.properties?.[prop]
      const oldVal = job?.properties?.[prop]
      if (newVal === null && oldVal != null) {
        jobDifferences = [...jobDifferences, { op: 'remove', path: `/properties/${prop}` }]
      }
    }

    dispatch(
      jobActions.updateJobByPath(jobDifferences, (_succ: boolean) => {
        setLoading(false)
      })
    )
  }

  return (
    <PrivateLoader loading={pageLoading}>
      <>
        <Box
          display="flex"
          flexDirection="row"
          gap={2}
          justifyContent="space-between"
        >
          <BoxContainer
            borderColor="#FFFFFF"
            sx={{
              width: '100%',
              justifyContent: 'flex-start',
            }}
            title={
              <TitleFirst
                alignItems="center"
                black="Assigned"
                blackVariant="h5"
                lineHeight="24px"
              />
            }
          >
            <Grid container spacing={1} sx={{ paddingLeft: '0.25rem' }}>
              <Grid item xs={6}>
                <LabeledText labelValue="Territory:" />
                <SelectInput
                  value={territoriesOptions.find(
                    (territory) =>
                      territory.key === jobInfo?.properties?.territory?.id
                  )}
                  options={territoriesOptions}
                  size="small"
                  labelVariant="h6"
                  onChange={(newValue) =>
                    handleOnChangeValue('properties/territory/id', newValue.key)
                  }
                  disabled={disableInputs}
                />
              </Grid>
              <Grid item xs={6}>
                <LabeledText labelValue="Affiliate:" />
                <SelectInput
                  //label="Affiliate:"
                  value={
                    affiliatesStages.find(
                      (affiliate) => affiliate.key === jobInfo?.properties?.affiliate
                    )
                  }
                  options={affiliatesStages}
                  size="small"
                  labelVariant="h6"
                  onChange={(newValue) =>
                    handleOnChangeValue('properties/affiliate', newValue.key)
                  }
                  disabled={disableInputs}
                />
              </Grid>
              <Grid item xs={12}>
                <LabeledText labelValue="Channel:" />
                <SelectInput
                  value={AFFILIATE_TYPE_OPTIONS.find((affiliateType) => affiliateType.key === jobInfo?.properties.affiliateType)}
                  options={AFFILIATE_TYPE_OPTIONS}
                  size="small"
                  labelVariant="h6"
                  onChange={(newValue) => handleOnChangeValue('properties/affiliateType', newValue.key)}
                  disabled={disableInputs}
                />
              </Grid>
              <Grid item xs={12}>
                <LabeledText labelValue="Access Info:" />
                <TextFieldLabel
                  value={jobInfo?.properties.accessDetails}
                  label=""
                  disabled={disableInputs}
                  multiline={true}
                  fullWidth={true}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleOnChangeValue(
                      'properties/accessDetails',
                      event.target.value
                    )
                  }
                  name="access-info-assignments"
                  type="text"
                  id="access-info-assignments"
                />
              </Grid>
              <Grid item xs={12}>
                <LabeledText labelValue="Lockbox:" />
                <TextFieldLabel
                  value={jobInfo?.properties.lockbox}
                  label=""
                  disabled={disableInputs}
                  multiline={true}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleOnChangeValue(
                      'properties/lockbox',
                      event.target.value
                    )
                  }
                  name="lockbox-assignments"
                  type="text"
                  id="lockbox-assignments"
                />
              </Grid>
              <Grid item xs={12}>
                <LabeledText labelValue="Preferred Start Date:" />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <DatePicker
                    minDate={new Date()}
                    value={jobInfo?.properties?.preferredStartDate ? formatTimestampDate(jobInfo.properties.preferredStartDate) : null}
                    onChange={(date: Date | null) =>
                      handleOnChangeValue('properties/preferredStartDate', combinePreferredDateAndWindow(date, preferredWindow, timezone))
                    }
                    disabled={disableInputs || flexibleStartDate}
                    disabledDates={disabledDates}
                    size="small"
                    style={{ height: '30px', padding: '0px', width: '300px' }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Checkbox
                      checked={flexibleStartDate}
                      onChange={() => handleFlexibleStartDate(!flexibleStartDate)}
                      name="flexible"
                      color="primary"
                      disabled={disableInputs}
                      style={{ padding: '0px', height: 'fit-content' }}
                    />
                    <Typography sx={{ fontWeight: 'bold' }}>Flexible</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Checkbox
                      checked={preferredWindow === 'morning'}
                      onChange={() => handlePreferredWindowChange('morning')}
                      name="preferredMorning"
                      color="primary"
                      disabled={disableInputs || flexibleStartDate}
                      style={{ padding: '0px', height: 'fit-content' }}
                    />
                    <Typography>Morning (8am - 12pm)</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Checkbox
                      checked={preferredWindow === 'afternoon'}
                      onChange={() => handlePreferredWindowChange('afternoon')}
                      name="preferredAfternoon"
                      color="primary"
                      disabled={disableInputs || flexibleStartDate}
                      style={{ padding: '0px', height: 'fit-content' }}
                    />
                    <Typography>Afternoon (12pm - 5pm)</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <LabeledText labelValue="Preferred Completion Date:" />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DatePicker
                    value={jobInfo?.properties?.preferredCompletedOn ? formatTimestampDate(jobInfo.properties.preferredCompletedOn) : null}
                    onChange={(date: Date | null) =>
                      handleOnChangeValue('properties/preferredCompletedOn', date ? formatDateTimestamp(date) : null)
                    }
                    disabled={disableInputs || flexibleCompletedOn}
                    disabledDates={[...disabledDates, ...getDatesBetween(addDays(new Date(), -37000), jobInfo?.properties?.preferredStartDate ? formatTimestampDate(jobInfo.properties.preferredStartDate) : new Date())]}
                    size="small"
                    style={{ height: '30px', padding: '0px', width: '300px' }}
                  />
                  <Checkbox
                    checked={flexibleCompletedOn}
                    onChange={() => handleFlexibleCompletedOn(!flexibleCompletedOn)}
                    name="flexible"
                    color="primary"
                    disabled={disableInputs}
                    style={{ padding: '0px', height: 'fit-content' }}
                  />
                  <Typography sx={{ fontWeight: 'bold' }}>Flexible</Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  label={<Typography variant="body2">Occupied</Typography>}
                  control={
                    <Checkbox
                      checked={jobInfo?.properties?.isOccupied ?? false}
                      onChange={(e) => handleOnChangeValue('properties/isOccupied', e.target.checked)}
                      color="primary"
                      disabled={disableInputs}
                    />
                  }
                />
                <FormControlLabel
                  label={<Typography variant="body2">Children under 18</Typography>}
                  control={
                    <Checkbox
                      checked={jobInfo?.properties?.hasChildrenUnderAge ?? false}
                      onChange={(e) => handleOnChangeValue('properties/hasChildrenUnderAge', e.target.checked)}
                      color="primary"
                      disabled={disableInputs}
                    />
                  }
                />
                <FormControlLabel
                  label={<Typography variant="body2">Pets</Typography>}
                  control={
                    <Checkbox
                      checked={jobInfo?.properties?.hasPets ?? false}
                      onChange={(e) => handleOnChangeValue('properties/hasPets', e.target.checked)}
                      color="primary"
                      disabled={disableInputs}
                    />
                  }
                />
              </Grid>
            </Grid>
          </BoxContainer>
          <BoxContainer
            borderColor="#FFFFFF"
            sx={{
              width: '100%',
              height: '340px',
              justifyContent: 'flex-start',
            }}
            title={
              <TitleFirst
                alignItems="center"
                black="Assign & Adjustments"
                blackVariant="h5"
                lineHeight="24px"
              />
            }
          >
            <Grid container spacing={1} sx={{ paddingLeft: '0.25rem' }}>
              <Grid item xs={6}>
                <LabeledText labelValue="Territory Manager:" />
                <SelectInput
                  //label="Territory Manager:"
                  value={territoryManagers.find(
                    (employee) => employee.key === jobInfo?.territoryManagerId
                  )}
                  options={territoryManagers}
                  size="small"
                  onChange={(newValue) =>
                    handleOnChangeValue('territoryManagerId', newValue.key)
                  }
                  labelVariant="h6"
                  disabled={disableInputs}
                />
              </Grid>
              <Grid item xs={6}>
                <LabeledText labelValue="Success Manager:" />
                <SelectInput
                  //label="Success Manager:"
                  value={
                    territorySuccessManagers.find(
                      (employee) => employee.key === jobInfo?.successManagerId
                    ) || null
                  }
                  options={territorySuccessManagers}
                  size="small"
                  labelVariant="h6"
                  onChange={(newValue) => {
                    handleOnChangeValue('successManagerId', newValue.key)
                  }}
                  disabled={disableInputs}
                />
              </Grid>
              <Grid item xs={6}>
                <LabeledText labelValue="Supervisor:" />
                <SelectInput
                  //label="Supervisor:"
                  value={supervisors.find(
                    (supervisor) => supervisor.key === jobInfo?.supervisorId
                  )}
                  options={supervisors}
                  size="small"
                  labelVariant="h6"
                  onChange={(newValue) =>
                    handleOnChangeValue('supervisorId', newValue.key)
                  }
                  disabled={disableInputs}
                />
              </Grid>
              <Grid item xs={6}>
                <LabeledText labelValue="Client person:" />
                <SelectInput
                  //label="Client person:"
                  value={clientPersons.find(
                    (client) => client.key === jobInfo?.customerCareEmployeeId
                  )}
                  options={clientPersons}
                  size="small"
                  labelVariant="h6"
                  onChange={(newValue) =>
                    handleOnChangeValue('customerCareEmployeeId', newValue.key)
                  }
                  disabled={disableInputs}
                />
              </Grid>
              <Grid item xs={6}>
                <LabeledText labelValue="Superintendent:" />
                <SelectInput
                  //label="Superintendent:"
                  value={
                    superIntendents.find(
                      (territory) => territory.key === jobInfo?.superintendentId
                    ) || null
                  }
                  options={superIntendents}
                  size="small"
                  labelVariant="h6"
                  onChange={(newValue) =>
                    handleOnChangeValue('superintendentId', newValue.key)
                  }
                  disabled={disableInputs}
                />
              </Grid>
              <Grid item xs={6}>
                <LabeledText labelValue="Client Project Manager:" />
                {/* <TextFieldLabel
                  value={jobInfo?.clientProjectManagerId}
                  label=""
                  fullWidth={true}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleOnChangeValue(
                      'clientProjectManagerId',
                      event.target.value
                    )
                  }
                  name="client-project-manager-assignments"
                  type="text"
                  id="client-project-manager-assignments"
                /> */}
                <SelectInput
                  //label="Client person:"
                  value={clientProjectManagers.find(
                    (client) => client.key === jobInfo?.clientProjectManagerId
                  )}
                  options={clientProjectManagers}
                  size="small"
                  labelVariant="h6"
                  onChange={(newValue) =>
                    handleOnChangeValue('clientProjectManagerId', newValue.key)
                  }
                  disabled={disableInputs}
                />
              </Grid>
              <Grid item xs={6}>
                <LabeledText labelValue="BD Manager:" />
                <SelectInput
                  //label="BD Manager:"
                  value={bdManagers.find(
                    (manager) =>
                      manager.key === jobInfo?.businessDevelopmentManagerId
                  )}
                  options={bdManagers}
                  size="small"
                  labelVariant="h6"
                  onChange={(newValue) =>
                    handleOnChangeValue(
                      'businessDevelopmentManagerId',
                      newValue.key
                    )
                  }
                  disabled={disableInputs}
                />
              </Grid>
            </Grid>
          </BoxContainer>

          <BoxContainer
            borderColor="#FFFFFF"
            sx={{
              width: '100%',
              height: '340px',
              justifyContent: 'flex-start',
            }}
            title={
              <TitleFirst
                alignItems="center"
                black="Dates"
                blackVariant="h5"
                lineHeight="24px"
              />
            }
          >
            <Grid
              container
              direction="column"
              justifyContent="space-between"
              spacing={2}
              sx={{ paddingLeft: '0.25rem' }}
            >
              <Grid item>
                <Grid
                  container
                  direction="column"
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Grid item xs={6}>
                    <Grid container justifyContent="space-between" spacing={2}>
                      <Grid item xs={6}>
                        <LabeledText labelValue="Delivery Date:" />
                        <DatePicker
                          name="estimateDeliveredOn"
                          value={
                            jobInfo?.properties.estimateDeliveredOn
                              ? formatTimestampDate(
                                jobInfo?.properties.estimateDeliveredOn
                              )
                              : null
                          }
                          onChange={(selectedDate) =>
                            handleOnChangeValue(
                              'properties/estimateDeliveredOn',
                              formatDateTimestamp(selectedDate)
                            )
                          }
                          size="small"
                          style={{ height: '30px', padding: '0px' }}
                          disabled={disableInputs}
                          align="right"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <LabeledText labelValue="Closing Date:" />
                        <DatePicker
                          name="closingDate"
                          value={
                            jobInfo?.properties.closingDate
                              ? formatTimestampDate(
                                jobInfo?.properties.closingDate
                              )
                              : null
                          }
                          onChange={(selectedDate) =>
                            handleOnChangeValue(
                              'properties/closingDate',
                              new Date(selectedDate).setHours(12) / 1000
                            )
                          }
                          size="small"
                          style={{ height: '30px', padding: '0px' }}
                          disabled={disableInputs}
                          align="right"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={6}>
                    <Grid container justifyContent="space-between" spacing={2}>
                      <Grid item xs={6}>
                        <LabeledText labelValue="Deadline:" />
                        <DatePicker
                          value={
                            jobInfo?.dueOn
                              ? formatTimestampDate(jobInfo?.dueOn)
                              : null
                          }
                          onChange={(selectedDate) => {
                            handleOnChangeValue(
                              'dueOn',
                              formatDateTimestamp(selectedDate)
                            )
                          }}
                          size="small"
                          style={{ height: '30px', padding: '0px' }}
                          disabled={disableInputs}
                          align="right"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <LabeledText labelValue="Completion ETA:" />
                        <DatePicker
                          value={
                            jobInfo?.expectedCompletedOn
                              ? formatTimestampDate(
                                jobInfo?.expectedCompletedOn
                              )
                              : null
                          }
                          onChange={(selectedDate) => {
                            handleOnChangeValue(
                              'expectedCompletedOn',
                              formatDateTimestamp(selectedDate)
                            )
                          }}
                          size="small"
                          style={{ height: '30px', padding: '0px' }}
                          disabled={disableInputs}
                          align="right"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item>
                <Grid
                  container
                  direction="column"
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Grid item xs={6}>
                    <Grid container justifyContent="space-between" spacing={2}>
                      <Grid item xs={6}>
                        <LabeledText labelValue="Scheduling Start:" />
                        <DatePicker
                          value={
                            jobInfo?.schedulingStartDate
                              ? formatTimestampDate(
                                jobInfo?.schedulingStartDate
                              )
                              : null
                          }
                          onChange={(selectedDate) => {
                            handleOnChangeValue(
                              'schedulingStartDate',
                              formatDateTimestamp(selectedDate)
                            )
                          }}
                          size="small"
                          style={{ height: '30px', padding: '0px' }}
                          disabled={disableInputs}
                          align="right"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <LabeledText labelValue="Scheduling End:" />
                        <DatePicker
                          value={
                            jobInfo?.schedulingEndDate
                              ? formatTimestampDate(jobInfo?.schedulingEndDate)
                              : null
                          }
                          onChange={(selectedDate) => {
                            handleOnChangeValue(
                              'schedulingEndDate',
                              formatDateTimestamp(selectedDate)
                            )
                          }}
                          size="small"
                          style={{ height: '30px', padding: '0px' }}
                          disabled={disableInputs}
                          align="right"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={6}>
                    <Grid container justifyContent="space-between" spacing={2}>
                      <Grid item xs={6}>
                        <LabeledText labelValue="Pre-Con Inspection:" />
                        <DatePicker
                          value={
                            jobInfo?.preConInspectionDate
                              ? formatTimestampDate(
                                jobInfo?.preConInspectionDate
                              )
                              : null
                          }
                          onChange={(selectedDate) => {
                            handleOnChangeValue(
                              'preConInspectionDate',
                              formatDateTimestamp(selectedDate)
                            )
                          }}
                          size="small"
                          style={{ height: '30px', padding: '0px' }}
                          disabled={disableInputs}
                          align="right"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <LabeledText labelValue="Final Walktrough:" />
                        <DatePicker
                          value={
                            jobInfo?.finalWalkthroughDate
                              ? formatTimestampDate(
                                jobInfo?.finalWalkthroughDate
                              )
                              : null
                          }
                          onChange={(selectedDate) => {
                            handleOnChangeValue(
                              'finalWalkthroughDate',
                              formatDateTimestamp(selectedDate)
                            )
                          }}
                          size="small"
                          style={{ height: '30px', padding: '0px' }}
                          disabled={disableInputs}
                          align="right"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </BoxContainer>
        </Box>
        {hasUpdatedData && (
          <ActionsButton
            variant="contained"
            sx={{
              position: 'fixed',
              height: '40px !important',
              width: '100PX',
              bottom: '40px',
              left: '40px',
            }}
            loading={loading}
            text="Save changes"
            onClick={onSubmit}
          />
        )}
      </>
    </PrivateLoader>
  )
}

export default Assignments
